import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/Layout/AdminLayout';
import { toast } from 'react-toastify';
import { FaTrashAlt, FaCalculator, FaHistory } from 'react-icons/fa';

// --- INTERFACES ---
interface Category {
  id: number;
  name: string;
  parentId?: number | null;
  productCount?: number; 
}

interface Product {
  id: number;
  productName: string;
  measurementUnit: string;
  typeQuantity: number | string;
  price: number;
}

interface SelectedProduct extends Product {
  quantity: number;
  itemDiscountMode: 'PERCENTAGE' | 'AMOUNT';
  itemDiscountValue: number;
  finalItemPrice: number;
}

interface Lead {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  remark?: string;
  nextSchedule?: string; 
  alternateMobile?: string;
  houseNumber?: string;
  streetArea?: string;
  landmark?: string;
  city?: string;
  state?: string;
  pincode?: string;
  country?: string;
}

interface BackendPincodeResponse {
  pincode?: string;
  city?: string;
  state?: string;
  country?: string;
}

const API_BASE_URL = 'https://www.ayushvatika.com/api';
// const PINCODE_API_BASE_URL = 'http://localhost:8484/api';
const PINCODE_API_BASE_URL = 'https://www.ayushvatika.com/api';

const SalesPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [lead, setLead] = useState<Lead | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  
  // --- Form State ---
  const [remark, setRemark] = useState<string>('');
  const [otherRemark, setOtherRemark] = useState<string>('');
  
  // History State
  const [previousRemark, setPreviousRemark] = useState<string>('');
  const [previousSchedule, setPreviousSchedule] = useState<string>('');

  // Address/User State
  const [name, setName] = useState<string>('');
  const [alternateMobile, setAlternateMobile] = useState<string>('');
  const [houseNumber, setHouseNumber] = useState<string>('');
  const [streetArea, setStreetArea] = useState<string>('');
  const [landmark, setLandmark] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [state, setState] = useState<string>('');
  const [pincode, setPincode] = useState<string>('');
  const [country, setCountry] = useState<string>('');
  const [pincodeFetching, setPincodeFetching] = useState<boolean>(false);

  // Schedule State
  const [nextScheduleDate, setNextScheduleDate] = useState<string>('');
  const [nextScheduleTime, setNextScheduleTime] = useState<string>('');
  const [showNextSchedule, setShowNextSchedule] = useState<boolean>(false);
  
  // --- Product Selection State ---
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [rootCategories, setRootCategories] = useState<Category[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState<number | null>(null);
  
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
  
  // --- Global Discount State ---
  const [globalDiscountMode, setGlobalDiscountMode] = useState<'PERCENTAGE' | 'AMOUNT'>('PERCENTAGE');
  const [globalDiscountInput, setGlobalDiscountInput] = useState<number>(0);
  const [orderPaidAmount, setOrderPaidAmount] = useState<number>(0);

  const isSoldRemark = remark === 'Sold';

  // --- ROLE CHECK LOGIC ---
  const checkIsAgent = () => {
    const token = localStorage.getItem('token');
    if (!token) return false;
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const roleStr = JSON.stringify(payload).toUpperCase();
        return roleStr.includes('AGENT');
    } catch(e) {
        return false;
    }
  };
  const isAgent = checkIsAgent();

  // --- NUMBER MASKING LOGIC ---
  const maskMobile = (mobile: string | undefined) => {
    if (!mobile || mobile.length < 10) return mobile || 'N/A';
    return mobile.substring(0, 2) + "******" + mobile.substring(8);
  };

  // --- Helpers ---
  const today = new Date().toISOString().split('T')[0];

  const subCategoriesForSelected = useMemo(() => {
    if (selectedCategoryId) {
      const subs = allCategories.filter(cat => cat.parentId === selectedCategoryId);
      subs.sort((a, b) => a.name.localeCompare(b.name));
      return subs;
    }
    return [];
  }, [selectedCategoryId, allCategories]);

  const fetchPincodeDetails = async (pin: string, showErrorToast: boolean = false) => {
    const cleanPin = pin.replace(/\D/g, '').slice(0, 6);

    if (cleanPin.length !== 6) return;

    try {
      setPincodeFetching(true);

      const response = await fetch(`${PINCODE_API_BASE_URL}/pincode/${cleanPin}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Pincode not found');
      }

      const data: BackendPincodeResponse = await response.json();

      if (data && (data.city || data.state || data.country)) {
        setCity(data.city || '');
        setState(data.state || '');
        setCountry(data.country || 'India');
      } else {
        setCity('');
        setState('');
        setCountry('');

        if (showErrorToast) {
          toast.error('Invalid pincode or address not found');
        }
      }
    } catch (error) {
      console.error('Pincode lookup failed:', error);
      setCity('');
      setState('');
      setCountry('');

      if (showErrorToast) {
        toast.error('Unable to fetch city/state from pincode');
      }
    } finally {
      setPincodeFetching(false);
    }
  };

  const handlePincodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const onlyNumbers = e.target.value.replace(/\D/g, '').slice(0, 6);
    setPincode(onlyNumbers);

    if (onlyNumbers.length < 6) {
      setCity('');
      setState('');
      setCountry('');
    }

    if (onlyNumbers.length === 6) {
      fetchPincodeDetails(onlyNumbers, true);
    }
  };

  // --- API Calls ---
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/categories`);
      if (response.ok) {
        const data: Category[] = await response.json();
        setAllCategories(data);
        const roots = data.filter(cat => !cat.parentId);
        roots.sort((a, b) => a.name.localeCompare(b.name));
        setRootCategories(roots);
      }
    } catch (error) { console.error(error); }
  };

  const fetchProductsBySubCategory = async (subCategoryId: number) => {
    setLoading(true);
    setAvailableProducts([]);
    try {
        const authToken = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/products/category/id/${subCategoryId}`, {
            headers: { 'Authorization': `Bearer ${authToken}` },
        });
        if (response.ok) {
            const data = await response.json();
            setAvailableProducts(data);
        }
    } catch (err) { console.error(err); } 
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCategories(); }, []);

  useEffect(() => {
    if (selectedSubCategoryId) fetchProductsBySubCategory(selectedSubCategoryId);
    else setAvailableProducts([]);
  }, [selectedSubCategoryId]);

  useEffect(() => {
    const fetchLeadDetails = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const authToken = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/admin/leads/details/${id}`, {
          headers: { 'Authorization': `Bearer ${authToken}` },
        });
        if (!response.ok) throw new Error('Failed');
        const data: Lead = await response.json();
        setLead(data);
        
        setName(`${data.firstName} ${data.lastName}`);
        setAlternateMobile(data.alternateMobile || '');
        setHouseNumber(data.houseNumber || '');
        setStreetArea(data.streetArea || '');
        setLandmark(data.landmark || '');
        setCity(data.city || '');
        setState(data.state || '');
        setPincode(data.pincode || '');
        setCountry(data.country || '');
        
        if (data.remark) setPreviousRemark(data.remark);
        if (data.nextSchedule) setPreviousSchedule(data.nextSchedule);
      } catch (err) { console.error(err); } 
      finally { setLoading(false); }
    };
    fetchLeadDetails();
  }, [id]);

  useEffect(() => {
    const cleanedPincode = pincode.replace(/\D/g, '').slice(0, 6);

    if (cleanedPincode.length !== 6) return;

    const timer = window.setTimeout(() => {
      fetchPincodeDetails(cleanedPincode, false);
    }, 400);

    return () => {
      window.clearTimeout(timer);
    };
  }, [pincode]);

  // --- Handlers ---
  const handleRemarkChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRemark(e.target.value);
    setShowNextSchedule(e.target.value === 'Call Back Scheduled');
    if (e.target.value !== 'Sold') {
        setSelectedProducts([]);
    }
  };

  const handleProductSelection = (product: Product, isChecked: boolean) => {
    if (isChecked) {
      setSelectedProducts(prev => [
        ...prev,
        { 
            ...product, 
            quantity: 1, 
            itemDiscountMode: 'PERCENTAGE',
            itemDiscountValue: 0,
            finalItemPrice: product.price 
        }
      ]);
    } else {
      setSelectedProducts(prev => prev.filter(p => p.id !== product.id));
    }
  };

  const updateProductRow = (productId: number, field: keyof SelectedProduct, value: any) => {
      setSelectedProducts(prev => prev.map(p => {
          if (p.id !== productId) return p;
          const updatedProduct = { ...p, [field]: value };
          
          const baseTotal = updatedProduct.price * updatedProduct.quantity;
          let discountAmt = 0;

          if (updatedProduct.itemDiscountMode === 'PERCENTAGE') {
              discountAmt = (baseTotal * updatedProduct.itemDiscountValue) / 100;
          } else {
              discountAmt = updatedProduct.itemDiscountValue;
          }

          if (discountAmt > baseTotal) discountAmt = baseTotal;

          updatedProduct.finalItemPrice = baseTotal - discountAmt;
          return updatedProduct;
      }));
  };

  // --- CALCULATION ENGINE ---
  const orderSummary = useMemo(() => {
    const subTotalAfterItemDiscounts = selectedProducts.reduce((sum, p) => sum + p.finalItemPrice, 0);
    const totalOriginalPrice = selectedProducts.reduce((sum, p) => sum + (p.price * p.quantity), 0);

    let globalDiscountAmt = 0;
    if (globalDiscountMode === 'PERCENTAGE') {
        globalDiscountAmt = (subTotalAfterItemDiscounts * globalDiscountInput) / 100;
    } else {
        globalDiscountAmt = globalDiscountInput;
    }

    if (globalDiscountAmt > subTotalAfterItemDiscounts) globalDiscountAmt = subTotalAfterItemDiscounts;

    const finalPayable = subTotalAfterItemDiscounts - globalDiscountAmt;

    const totalDiscountGiven = totalOriginalPrice - finalPayable;
    const discountPercentageForBackend = totalOriginalPrice > 0 
        ? (totalDiscountGiven / totalOriginalPrice) * 100 
        : 0;

    return {
        totalOriginalPrice,
        subTotalAfterItemDiscounts,
        globalDiscountAmt,
        finalPayable: Math.round(finalPayable),
        totalDiscountGiven,
        discountPercentageForBackend
    };
  }, [selectedProducts, globalDiscountMode, globalDiscountInput]);

  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
        const authToken = localStorage.getItem('token');
        
        if (isSoldRemark) {
            if (selectedProducts.length === 0) {
                toast.error('Please select at least one product.');
                setLoading(false);
                return;
            }

            const salePayload = {
                name,
                email: lead?.email,
                mobile: lead?.mobile,
                alternateMobile,
                houseNumber, streetArea, landmark, city, state, pincode, country,
                remark: 'Sold',
                medicine: selectedProducts.map(p => `${p.productName} (Qty: ${p.quantity})`).join(', '),
                medicineUnit: selectedProducts.map(p => `${p.typeQuantity}${p.measurementUnit}`).join(', '),
                medicineQuantity: selectedProducts.reduce((acc, p) => acc + p.quantity, 0),
                medicinePrice: Math.round(orderSummary.totalOriginalPrice),
                discountPercentage: parseFloat(orderSummary.discountPercentageForBackend.toFixed(2)),
                discountAmount: parseFloat(orderSummary.totalDiscountGiven.toFixed(2)),
                paidAmount: orderPaidAmount > 0 ? orderPaidAmount : orderSummary.finalPayable,
                payableAmount: orderSummary.finalPayable
            };

            await fetch(`${API_BASE_URL}/admin/leads/saveOrUpdate/${id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
                body: JSON.stringify(salePayload)
            });
            toast.success('Sale Order Confirmed!');
        } else {
            const updatePayload = {
                name,
                remark: remark === 'other' ? otherRemark : remark,
                nextSchedule: showNextSchedule ? `${nextScheduleDate}T${nextScheduleTime}` : null,
                alternateMobile, houseNumber, streetArea, landmark, city, state, pincode, country
            };
            await fetch(`${API_BASE_URL}/admin/leads/saveOrUpdate/${id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
                body: JSON.stringify(updatePayload)
            });
            toast.success('Lead Details Updated!');
        }
        navigate('/admin/data/hot-data');
    } catch (error) {
        toast.error('Operation failed');
    } finally {
        setLoading(false);
    }
  };

  if (loading && !lead) return <AdminLayout><div className="text-gray-900 dark:text-white p-8">Loading...</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="container mx-auto p-6 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white min-h-screen transition-colors duration-200">
        <h2 className="text-3xl font-bold mb-6 text-blue-600 dark:text-green-500">Manage Lead : {lead?.firstName} {lead?.lastName}</h2>

        <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 transition-colors duration-200">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            
            {/* --- LEFT COLUMN --- */}
            <div className="space-y-4">
               <h3 className="text-xl font-semibold border-b border-gray-300 dark:border-gray-600 pb-2 mb-4 text-gray-700 dark:text-gray-200">User & Address</h3>
               <div className="grid grid-cols-2 gap-3">
                 <input value={name} onChange={e=>setName(e.target.value)} className="input-field col-span-1" placeholder="Full Name *" required={isSoldRemark}/>
                 
                 {/* --- CONDITIONAL MOBILE INPUT --- */}
                 <input 
                     value={isAgent ? maskMobile(lead?.mobile) : lead?.mobile} 
                     readOnly 
                     title={isAgent ? "Number is masked for security" : ""}
                     className="input-field col-span-1 opacity-60 cursor-not-allowed bg-gray-200 dark:bg-gray-700" 
                 />

                 <input value={alternateMobile} onChange={e=>setAlternateMobile(e.target.value)} className="input-field col-span-1" placeholder="Alt Mobile" />
                 <input value={houseNumber} onChange={e=>setHouseNumber(e.target.value)} className="input-field col-span-1" placeholder="House No *" required={isSoldRemark}/>
                 <input value={streetArea} onChange={e=>setStreetArea(e.target.value)} className="input-field col-span-2" placeholder="Street/Area *" required={isSoldRemark}/>
                 <input value={city} onChange={e=>setCity(e.target.value)} className="input-field col-span-1" placeholder="City *" required={isSoldRemark}/>
                 <input value={state} onChange={e=>setState(e.target.value)} className="input-field col-span-1" placeholder="State *" required={isSoldRemark}/>
                 <input
                    value={pincode}
                    onChange={handlePincodeChange}
                    maxLength={6}
                    inputMode="numeric"
                    className="input-field col-span-1"
                    placeholder={pincodeFetching ? 'Fetching...' : 'Pincode *'}
                    required={isSoldRemark}
                 />
                 <input value={country} onChange={e=>setCountry(e.target.value)} className="input-field col-span-1" placeholder="Country" required={isSoldRemark}/>
               </div>
            </div>

            {/* --- RIGHT COLUMN --- */}
            <div className="space-y-4">
              <h3 className="text-xl font-semibold border-b border-gray-300 dark:border-gray-600 pb-2 mb-4 text-gray-700 dark:text-gray-200">Action & Remarks</h3>
              
              {previousRemark && (
                  <div className="bg-yellow-50 dark:bg-gray-700/50 p-3 rounded border-l-4 border-yellow-500 flex justify-between items-center mb-4">
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-bold">Last Interaction</p>
                        <p className="text-yellow-700 dark:text-yellow-400 font-medium text-lg">{previousRemark}</p>
                      </div>
                      {previousSchedule && (
                          <div className="text-right">
                              <p className="text-xs text-gray-500 dark:text-gray-400 font-bold"><FaHistory className="inline mr-1"/>Scheduled</p>
                              <p className="text-gray-800 dark:text-white text-sm">
                                  {new Date(previousSchedule).toLocaleDateString()} 
                                  <span className="ml-2 bg-gray-200 dark:bg-gray-600 px-1 rounded text-xs">
                                    {new Date(previousSchedule).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                  </span>
                              </p>
                          </div>
                      )}
                  </div>
              )}

              <div className="bg-gray-100 dark:bg-gray-700/30 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                <select value={remark} onChange={handleRemarkChange} className="input-field mb-3 w-full text-gray-900 dark:text-white bg-white dark:bg-gray-700" required>
                    <option value="">Select Action...</option>
                    <option value="Not Interested">Not Interested</option>
                    <option value="Call Back Scheduled">Call Back Scheduled</option>
                    <option value="Call Not Pick">Call Not Pick</option>
                    <option value="Switch off / Not Reachable">Switch off / Not Reachable</option>
                    <option value="Sold" className="font-bold text-green-600 dark:text-green-400">Order</option>
                    <option value="other">Other</option>
                </select>

                {showNextSchedule && (
                    <div className="flex gap-2 animate-fadeIn mt-2">
                        <input type="date" min={today} value={nextScheduleDate} onChange={e=>setNextScheduleDate(e.target.value)} className="input-field w-1/2" required />
                        <input type="time" value={nextScheduleTime} onChange={e=>setNextScheduleTime(e.target.value)} className="input-field w-1/2" required />
                    </div>
                )}
                
                {remark === 'other' && (
                    <input type="text" value={otherRemark} onChange={e=>setOtherRemark(e.target.value)} placeholder="Enter Remark" className="input-field mt-2 w-full" />
                )}
              </div>

              {/* PRODUCT SELECTION */}
              {isSoldRemark && (
                <div className="animate-fadeIn mt-4 pt-4 border-t border-gray-300 dark:border-gray-600">
                  <h4 className="text-green-600 dark:text-green-400 font-bold mb-3 flex items-center gap-2">
                    <FaCalculator /> Add Products
                  </h4>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <select onChange={(e) => {
                        setSelectedCategoryId(Number(e.target.value));
                        setSelectedSubCategoryId(null);
                    }} className="input-field text-sm">
                      <option value="">Category...</option>
                      {rootCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    
                    <select onChange={(e) => {
                        const val = Number(e.target.value);
                        setSelectedSubCategoryId(val);
                        fetchProductsBySubCategory(val);
                    }} disabled={!selectedCategoryId} className="input-field text-sm">
                      <option value="">Sub Category...</option>
                      {subCategoriesForSelected.map(c => (
                        <option key={c.id} value={c.id}>
                            {c.name} {c.productCount !== undefined ? `(${c.productCount})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Available Products List */}
                  {availableProducts.length > 0 && (
                      <div className="max-h-40 overflow-y-auto custom-scrollbar border border-gray-300 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-900/50">
                          {availableProducts.map(p => (
                              <div key={p.id} className="flex items-center justify-between p-2 hover:bg-gray-200 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-700 last:border-0">
                                  <div className="flex items-center gap-2">
                                      <input type="checkbox" 
                                          checked={selectedProducts.some(sp => sp.id === p.id)}
                                          onChange={(e) => handleProductSelection(p, e.target.checked)}
                                          className="w-4 h-4 rounded text-green-500 focus:ring-green-500 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600"
                                      />
                                      <div>
                                          <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{p.productName}</p>
                                          <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {p.typeQuantity}{p.measurementUnit} | ₹{p.price}
                                          </p>
                                      </div>
                                  </div>
                              </div>
                          ))}
                      </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* --- ORDER TABLE --- */}
          {isSoldRemark && selectedProducts.length > 0 && (
            <div className="mt-8 animate-slideUp">
                <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <h3 className="text-lg font-bold text-gray-800 dark:text-white p-4 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">Order Items & Discounts</h3>
                    
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left text-gray-600 dark:text-gray-300">
                            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 uppercase text-xs">
                                <tr>
                                    <th className="px-4 py-3">Product</th>
                                    <th className="px-4 py-3">Unit</th>
                                    <th className="px-4 py-3">Price</th>
                                    <th className="px-4 py-3 w-24">Qty</th>
                                    <th className="px-4 py-3 w-40">Item Discount</th>
                                    <th className="px-4 py-3 text-right">Total</th>
                                    <th className="px-4 py-3 text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {selectedProducts.map(item => (
                                    <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{item.productName}</td>
                                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                                            {item.typeQuantity}{item.measurementUnit}
                                        </td>
                                        <td className="px-4 py-3">₹{item.price}</td>
                                        <td className="px-4 py-3">
                                            <input 
                                                type="number" min="1" 
                                                value={item.quantity}
                                                onChange={(e) => updateProductRow(item.id, 'quantity', parseInt(e.target.value) || 1)}
                                                className="w-16 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-gray-900 dark:text-white text-center"
                                            />
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex gap-1">
                                                <select 
                                                    value={item.itemDiscountMode}
                                                    onChange={(e) => updateProductRow(item.id, 'itemDiscountMode', e.target.value)}
                                                    className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-xs rounded px-1"
                                                >
                                                    <option value="PERCENTAGE">%</option>
                                                    <option value="AMOUNT">₹</option>
                                                </select>
                                                <input 
                                                    type="number" min="0"
                                                    value={item.itemDiscountValue}
                                                    onChange={(e) => updateProductRow(item.id, 'itemDiscountValue', parseFloat(e.target.value) || 0)}
                                                    className="w-20 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-gray-900 dark:text-white text-xs"
                                                    placeholder="Disc"
                                                />
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-right font-bold text-green-600 dark:text-green-400">
                                            ₹{item.finalItemPrice.toFixed(2)}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <button type="button" onClick={() => handleProductSelection(item, false)} className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300">
                                                <FaTrashAlt />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="p-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex flex-col md:flex-row justify-between items-end gap-4">
                        <div className="w-full md:w-1/3">
                            <label className="text-xs text-gray-500 dark:text-gray-400 font-bold uppercase mb-1 block">Extra Global Discount</label>
                            <div className="flex gap-2">
                                <div className="flex items-center bg-white dark:bg-gray-700 rounded px-2 border border-gray-300 dark:border-gray-600">
                                    <label className="mr-3 cursor-pointer text-sm text-gray-700 dark:text-white"><input type="radio" checked={globalDiscountMode === 'PERCENTAGE'} onChange={()=>setGlobalDiscountMode('PERCENTAGE')} /> %</label>
                                    <label className="cursor-pointer text-sm text-gray-700 dark:text-white"><input type="radio" checked={globalDiscountMode === 'AMOUNT'} onChange={()=>setGlobalDiscountMode('AMOUNT')} /> ₹</label>
                                </div>
                                <input 
                                    type="number" min="0"
                                    value={globalDiscountInput}
                                    onChange={(e) => setGlobalDiscountInput(parseFloat(e.target.value) || 0)}
                                    className="flex-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-gray-900 dark:text-white"
                                    placeholder="Enter discount..."
                                />
                            </div>
                        </div>

                        <div className="w-full md:w-1/3 text-right space-y-1">
                            <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                                <span>Total (Before Disc):</span>
                                <span>₹{orderSummary.totalOriginalPrice}</span>
                            </div>
                            <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
                                <span>Total Savings:</span>
                                <span>- ₹{orderSummary.totalDiscountGiven.toFixed(2)}</span>
                            </div>
                            <div className="bg-purple-100 dark:bg-purple-900 text-gray-900 dark:text-white p-3 rounded mt-2 flex justify-between items-center shadow-lg border border-purple-200 dark:border-purple-800">
                                <span className="text-green-600 dark:text-green-400 font-extrabold text-xl uppercase">PAYABLE:</span>
                                <span className="text-2xl font-bold">₹{orderSummary.finalPayable}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
          )}

          <div className="mt-8 flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className={`px-6 py-3 rounded shadow-lg font-bold transition-all
                  ${isSoldRemark 
                      ? 'bg-green-600 hover:bg-green-500 text-white' 
                      : 'bg-blue-600 hover:bg-blue-500 text-white'}`
              }
            >
              {loading ? 'Processing...' : isSoldRemark ? 'Confirm Sale Order' : 'Update Lead'}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .input-field { width: 100%; padding: 10px; border-radius: 4px; background-color: #ffffff; color: #111827; border: 1px solid #d1d5db; outline: none; }
        .dark .input-field { background-color: #374151; color: white; border-color: #4b5563; }
        .input-field:focus { border-color: #3B82F6; }
        .input-field:disabled { cursor: not-allowed; opacity: 0.5; }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f3f4f6; }
        .dark .custom-scrollbar::-webkit-scrollbar-track { background: #111827; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 3px; }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #374151; }
        .animate-fadeIn { animation: fadeIn 0.3s ease-in-out; }
        .animate-slideUp { animation: slideUp 0.4s ease-out; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </AdminLayout>
  );
};

export default SalesPage;